import {
  findAllMediaByRatioLabel,
  findVideoMedia,
  findAllVideoMedia,
  humanizeCallToAction,
} from './format'
import { resolveCallToAction } from 'utils/helpers'

const RATIO_1X1_MOBILE_SKINS = [
  'gmail-chrome', // 31520:16708
  'gmail-chrome', // 31520:16752
]

const RATIO_1X1_DESKTOP_NATIVE = [
  { skin: 'native-article', youtubeBottom: false }, // 31522:15984 (sampled)
  { skin: 'native-article', youtubeBottom: true }, // 31522:16011 (no sampled sibling — youtube-bottom variant)
]

const RATIO_191_MOBILE_SKINS = [
  'gmail-chrome', // 31522:16056
  'gmail-chrome', // 31522:16099
  'gmail-chrome', // 31522:16214
]

const RATIO_191_DESKTOP_NATIVE = [
  { skin: 'native-article', youtubeBottom: true }, // 31522:16272 (unlabeled — flagged in ticket Context as needing confirmation; treated as the youtube-bottom variant)
  { skin: 'native-article', youtubeBottom: false }, // 31522:16303 desktop-1.91:1-demand
]

const RATIO_4X5_MOBILE_SKINS = [
  'gmail-chrome', // 31522:16448
  'gmail-chrome', // 31522:16491
]

// Google's Display/Demand Gen network only ever accepts a YouTube passthrough
// video, never an uploaded video file — see googleDisplayVariations.js's own
// isUploadedVideoMedia for the full reasoning. Meta's isVideoMedia/
// findVideoMedia (format.js) stay unchanged.
const isUploadedVideoMedia = (media) =>
  Boolean(media?.type?.includes('video')) && !media?.youtube_video_id

function getGoogleMedia(creative) {
  return (creative?.media ?? []).filter((media) => !isUploadedVideoMedia(media))
}

function buildDemandGenAd(creative, media, skin, t, extra = {}) {
  return {
    skin,
    headline: creative?.headline?.[0] ?? '',
    longHeadline: creative?.long_headline?.[0] ?? '',
    description: creative?.body?.[0] ?? creative?.description ?? '',
    callToAction: humanizeCallToAction(resolveCallToAction(creative?.call_to_action, 'google'), t),
    destinationUrl: creative?.destination_url?.[0] ?? '',
    displayUrlPath: creative?.display_url_path?.[0] ?? null,
    businessName: creative?.business_name ?? '',
    logo: creative?.logo ?? null,
    companionBanner: creative?.companion_banner ?? null,
    media,
    ...extra,
  }
}

function buildDesktopNativeAds(creative, media, slots, t) {
  const videoMedia = findVideoMedia(getGoogleMedia(creative))
  return slots.map(({ skin, youtubeBottom }) =>
    buildDemandGenAd(
      creative,
      media,
      skin,
      t,
      youtubeBottom && videoMedia ? { youtubeBottomMedia: videoMedia } : {},
    ),
  )
}

function buildRatioGroupAds(creative, mediaList, mobileSkins, desktopNativeSlots, t) {
  return mediaList.flatMap((media) => [
    ...mobileSkins.map((skin) => buildDemandGenAd(creative, media, skin, t)),
    ...buildDesktopNativeAds(creative, media, desktopNativeSlots, t),
  ])
}

// 9:16 is Demand Gen's vertical Shorts-style surface:
// - still 9:16 images → shorts-chrome (full-bleed brand/CTA overlay + tab bar)
// - YouTube videos → youtube-chrome (in-feed chrome). YouTube pixel dimensions
//   aren't known client-side, so videos are selected via findAllVideoMedia,
//   not ratio-matching. One card per distinct image/video.
export function getDemandGen9x16Variations(creative, t) {
  const mediaList = getGoogleMedia(creative)
  const imageAds = findAllMediaByRatioLabel(mediaList, '9:16').map((media) =>
    buildDemandGenAd(creative, media, 'shorts-chrome', t),
  )
  const videoAds = findAllVideoMedia(mediaList).map((media) =>
    buildDemandGenAd(creative, media, 'youtube-chrome', t),
  )
  return [...imageAds, ...videoAds]
}

// One group covers both the 1:1 "mobile" family (2 ids) and the 1:1
// "desktop native" family (2 ids) — placementGroups.js groups by ratio, not
// by individual leaf node, same rationale googleDisplayVariations.js
// documents for its own 191x1 group. One full skin-set per matching image.
export function getDemandGen1x1Variations(creative, t) {
  return buildRatioGroupAds(
    creative,
    findAllMediaByRatioLabel(getGoogleMedia(creative), '1:1'),
    RATIO_1X1_MOBILE_SKINS,
    RATIO_1X1_DESKTOP_NATIVE,
    t,
  )
}

// One group covers the 1.91:1 "mobile" family (3 ids) and "desktop native"
// family (2 ids) — same combined-by-ratio rationale as above.
export function getDemandGen191x1Variations(creative, t) {
  return buildRatioGroupAds(
    creative,
    findAllMediaByRatioLabel(getGoogleMedia(creative), '1.91:1'),
    RATIO_191_MOBILE_SKINS,
    RATIO_191_DESKTOP_NATIVE,
    t,
  )
}

export function getDemandGen4x5Variations(creative, t) {
  const images = findAllMediaByRatioLabel(getGoogleMedia(creative), '4:5')
  return images.flatMap((media) =>
    RATIO_4X5_MOBILE_SKINS.map((skin) => buildDemandGenAd(creative, media, skin, t)),
  )
}
